import React from "react";
import ServicePageShell from "../ServicePageShell/ServicePageShell";
import { useIsDanish } from "../../i18n/LanguageContext";

const MicrosoftAzureServices = () => {
  const isDa = useIsDanish();
  const offerings = [
    {
      title: "Cloud Architecture & Landing Zones",
      desc: "A clean Azure foundation: subscriptions, resource groups, naming, network patterns, identity, and governance — ready for scale.",
      icon: (
        <svg viewBox="0 0 24 24" className="icon" aria-hidden="true">
          <path
            d="M4 10.5 12 5l8 5.5V19a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-8.5Z"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinejoin="round"
          />
          <path
            d="M9 21v-7h6v7"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinejoin="round"
          />
        </svg>
      ),
    },
    {
      title: "Infrastructure as Code (Bicep)",
      desc: "Repeatable deployments with modules, parameters, environments (dev/pre/prd), and best-practice patterns — no click-ops.",
      icon: (
        <svg viewBox="0 0 24 24" className="icon" aria-hidden="true">
          <path
            d="M7 7 4 12l3 5M17 7l3 5-3 5"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M14 6 10 18"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
          />
        </svg>
      ),
    },
    {
      title: "Azure DevOps CI/CD",
      desc: "Pipelines that validate, what-if, deploy, and promote safely. Service connections, federated credentials, approvals, and environments.",
      icon: (
        <svg viewBox="0 0 24 24" className="icon" aria-hidden="true">
          <path
            d="M5 6h6l2 3h6v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2Z"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinejoin="round"
          />
          <path
            d="M9 13h6"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
          />
        </svg>
      ),
    },
    {
      title: "Identity, RBAC & PIM",
      desc: "Least-privilege access with role design, Entra ID groups, PIM activation, and auditing so humans + workloads get only what they need.",
      icon: (
        <svg viewBox="0 0 24 24" className="icon" aria-hidden="true">
          <path
            d="M12 12a4 4 0 1 0-4-4 4 4 0 0 0 4 4Z"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
          />
          <path
            d="M4 21a8 8 0 0 1 16 0"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
          />
          <path
            d="M19 8.5 21 10l-2 1.5"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      ),
    },
    {
      title: "Secure Networking",
      desc: "VNet design, subnets, NSGs, Private Endpoints, DNS, NAT Gateway, App Gateway + WAF — with patterns that keep services private by default.",
      icon: (
        <svg viewBox="0 0 24 24" className="icon" aria-hidden="true">
          <path
            d="M12 3 4 7v10l8 4 8-4V7l-8-4Z"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinejoin="round"
          />
          <path
            d="M12 3v18M4 7l8 4 8-4"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinejoin="round"
          />
        </svg>
      ),
    },
    {
      title: "Apps & Integration",
      desc: "App Service, Function Apps, Container Apps, APIM, Service Bus, Event Hubs — connected cleanly with observability and reliability baked in.",
      icon: (
        <svg viewBox="0 0 24 24" className="icon" aria-hidden="true">
          <path
            d="M7 7h10v10H7z"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
          />
          <path
            d="M4 12h3M17 12h3M12 4v3M12 17v3"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
          />
        </svg>
      ),
    },
    {
      title: "Monitoring & Observability",
      desc: "Application Insights, Log Analytics, dashboards, alerts, and KQL — so you can see issues early and prove what’s happening in production.",
      icon: (
        <svg viewBox="0 0 24 24" className="icon" aria-hidden="true">
          <path
            d="M4 19V5a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v14"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
          />
          <path
            d="M7 16l3-4 3 2 4-6"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M4 19h16"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
          />
        </svg>
      ),
    },
    {
      title: "Security Operations",
      desc: "Microsoft Defender for Cloud & Sentinel setup, detections, alert hygiene, and practical improvements (not checkbox security).",
      icon: (
        <svg viewBox="0 0 24 24" className="icon" aria-hidden="true">
          <path
            d="M12 3 20 7v6c0 5-3.5 9-8 10-4.5-1-8-5-8-10V7l8-4Z"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinejoin="round"
          />
          <path
            d="M9 12l2 2 4-5"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      ),
    },
  ];

  const pimRoles = [
    {
      group: "Core access (common)",
      items: ["Reader", "Contributor", "Owner", "User Access Administrator"],
    },
    {
      group: "Identity & security",
      items: [
        "Security Reader / Security Administrator",
        "Privileged Role Administrator (Entra ID / PIM operations where applicable)",
        "Key Vault Secrets User / Key Vault Secrets Officer",
      ],
    },
    {
      group: "Data & messaging",
      items: [
        "Storage Blob Data Contributor",
        "Azure Service Bus Data Sender / Data Receiver",
        "Azure Event Hubs Data Sender / Data Receiver",
      ],
    },
    {
      group: "Apps & operations",
      items: [
        "Website Contributor / App Service Contributor",
        "API Management Service Contributor (where needed)",
        "Monitoring Metrics Publisher (Container Apps / telemetry scenarios)",
        "Log Analytics Reader / Monitoring Reader",
      ],
    },
  ];

  const whyItMatters = [
    {
      title: "You need predictable deployments",
      desc: "IaC (Bicep) means environments stay consistent and changes are traceable.",
    },
    {
      title: "You need secure access without slowing teams down",
      desc: "PIM + RBAC gives just-in-time access and reduces permanent privileges.",
    },
    {
      title: "You need production-grade visibility",
      desc: "Logs, metrics, and alerts turn “guessing” into fast troubleshooting.",
    },
    {
      title: "You need networks that default to private",
      desc: "Private endpoints + DNS + gateway/WAF patterns reduce exposure.",
    },
  ];

  const whyItMattersDa = [
    {
      title: "I har brug for forudsigelige deployments",
      desc: "IaC med Bicep betyder, at miljøer forbliver konsistente, og ændringer kan spores.",
    },
    {
      title: "I har brug for sikker adgang uden at bremse teams",
      desc: "PIM og RBAC giver just-in-time adgang og reducerer permanente rettigheder.",
    },
    {
      title: "I har brug for produktionsklar synlighed",
      desc: "Logs, metrics og alerts gør gætteri til hurtig fejlfinding.",
    },
    {
      title: "I har brug for netværk der er private som standard",
      desc: "Private endpoints, DNS og gateway/WAF-mønstre reducerer eksponering.",
    },
  ];

  const currentWhyItMatters = isDa ? whyItMattersDa : whyItMatters;

  return (
    <ServicePageShell>
      <div className="az-wrap">
        {/* HERO */}
        <section className="az-hero">
          <div className="az-heroInner">
            <div className="az-heroText">
              <div className="az-pill">
                <span className="az-dot" /> Microsoft Azure Services
              </div>

              <h1 className="az-title">
                {isDa ? "Byg, sikr og drift Azure-platforme der kan skalere uden kaos." : "Build, secure, and run Azure platforms that scale — without chaos."}
              </h1>

              <p className="az-subtitle">
                {isDa ? "Fra landing zones og Bicep-moduler til DevOps pipelines, netværk, PIM/RBAC, APIM-integrationer og monitoring. Vi hjælper jer med at levere sikkert og drifte med ro i maven." : "From landing zones and Bicep modules to DevOps pipelines, networking, PIM/RBAC, APIM integrations, and monitoring — we help you ship safely and operate confidently."}
              </p>

              <div className="az-ctaRow">
                <a className="az-btnPrimary" href="/#contact-section">
                  {isDa ? "Tal med os" : "Talk to us"} <span aria-hidden="true">→</span>
                </a>
                <a className="az-btnGhost" href="/use-cases">
                  {isDa ? "Se cases" : "View use cases"}
                </a>
              </div>

              <div className="az-trust">
                <div className="az-trustItem">
                  <strong>{isDa ? "Sikkerhed" : "Security"}</strong>
                  <span>Least privilege</span>
                </div>
                <div className="az-divider" />
                <div className="az-trustItem">
                  <strong>Automation</strong>
                  <span>Bicep + CI/CD</span>
                </div>
                <div className="az-divider" />
                <div className="az-trustItem">
                  <strong>{isDa ? "Drift" : "Operations"}</strong>
                  <span>Logs + Alerts</span>
                </div>
              </div>
            </div>

            <div className="az-heroCard">
              <div className="az-cardHead">
                <h3>{isDa ? "Det I får" : "What you get"}</h3>
                <p>{isDa ? "Praktiske forbedringer med målbare resultater." : "Practical improvements with measurable outcomes."}</p>
              </div>

              <div className="az-miniGrid">
                <div className="az-mini">
                  <span className="az-miniLabel">{isDa ? "Fundament" : "Foundation"}</span>
                  <span className="az-miniValue">Landing zone</span>
                </div>
                <div className="az-mini">
                  <span className="az-miniLabel">Deployments</span>
                  <span className="az-miniValue">Bicep modules</span>
                </div>
                <div className="az-mini">
                  <span className="az-miniLabel">Access</span>
                  <span className="az-miniValue">RBAC + PIM</span>
                </div>
                <div className="az-mini">
                  <span className="az-miniLabel">Reliability</span>
                  <span className="az-miniValue">Monitoring</span>
                </div>
              </div>

              <div className="az-note">
                <span className="az-noteBadge">Tip</span>
                <span>
                  {isDa ? "Vi foretrækker mønstre hvor services er private som standard, og kun det nødvendige eksponeres." : "We prefer patterns that keep your services private by default — and expose only what must be exposed."}
                </span>
              </div>
            </div>
          </div>

          <div className="az-glow az-glow1" />
          <div className="az-glow az-glow2" />
        </section>

        {/* OFFERINGS */}
        <section className="az-section">
          <div className="az-sectionHead">
            <h2>{isDa ? "Azure-services vi leverer" : "Azure services we deliver"}</h2>
            <p>
              {isDa ? "Azure-arkitektur, automation, sikkerhed og drift, tilpasset jeres miljø." : "A full stack of Azure architecture, automation, security, and operations — tailored to your environment."}
            </p>
          </div>

          <div className="az-grid">
            {offerings.map((o) => (
              <div key={o.title} className="az-card">
                <div className="az-cardIcon">{o.icon}</div>
                <div className="az-cardBody">
                  <h3>{o.title}</h3>
                  <p>{o.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* WHY YOU NEED IT */}
        <section className="az-section az-why">
          <div className="az-whyInner">
            <div className="az-whyText">
              <h2>{isDa ? "Hvorfor det er nødvendigt" : "Why you need it"}</h2>
              <p>
                {isDa ? "Azure bliver dyrt og risikabelt, når det vokser uden struktur. Her er de områder der typisk giver størst effekt hurtigst." : "Azure becomes expensive and risky when it grows without structure. These are the areas that usually make the biggest difference fastest."}
              </p>
            </div>

            <div className="az-whyGrid">
              {currentWhyItMatters.map((x) => (
                <div key={x.title} className="az-whyCard">
                  <h3>{x.title}</h3>
                  <p>{x.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* PIM / ROLES */}
        <section className="az-section az-roles">
          <div className="az-sectionHead">
            <h2>{isDa ? "Eksempler på PIM- og RBAC-roller" : "PIM & RBAC role examples"}</h2>
            <p>
              {isDa ? "Vi designer roller omkring least privilege. PIM giver just-in-time adgang, audit trails og færre permanente rettigheder." : "We design roles around least privilege. PIM gives just-in-time access, audit trails, and fewer permanent permissions."}
            </p>
          </div>

          <div className="az-roleGrid">
            {pimRoles.map((r) => (
              <div key={r.group} className="az-roleCard">
                <div className="az-roleTitle">{r.group}</div>
                <ul className="az-roleList">
                  {r.items.map((i) => (
                    <li key={i}>
                      <span className="az-check" aria-hidden="true">
                        ✓
                      </span>
                      {i}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          <div className="az-banner">
            <div>
              <h3>{isDa ? "Vil I have en rollemodel der passer til organisationen?" : "Want a role model that fits your org?"}</h3>
              <p>
                {isDa ? "Vi kan kortlægge teams, ansvar og nødvendige rettigheder og implementere det med Entra groups og PIM." : "We can map teams → responsibilities → required permissions and implement it with Entra groups + PIM."}
              </p>
            </div>
            <a className="az-btnPrimary" href="/#contact-section">
              {isDa ? "Lad os designe det" : "Let’s design it"} <span aria-hidden="true">→</span>
            </a>
          </div>
        </section>

        {/* CTA */}
        <section className="az-section az-final">
          <div className="az-finalCard">
            <h2>{isDa ? "Klar til at forbedre jeres Azure-platform?" : "Ready to improve your Azure platform?"}</h2>
            <p>
              {isDa ? "Fortæl hvad I kører i dag, eller hvad I vil bygge. Vi vender tilbage med en klar plan uden fluff." : "Tell us what you’re running today (or what you want to build). We’ll respond with a clear plan — no fluff, no jargon."}
            </p>
            <div className="az-ctaRow">
              <a className="az-btnPrimary" href="/#contact-section">
                {isDa ? "Kontakt os" : "Contact us"} <span aria-hidden="true">→</span>
              </a>
              <a className="az-btnGhost" href="/use-cases">
                {isDa ? "Se ydelser" : "Explore services"}
              </a>
            </div>
          </div>
        </section>

        {/* Styles (matching the Software Development aesthetic) */}
        <style>{`
          .az-wrap{
            max-width: 1100px;
            margin: 0 auto;
            padding: 28px 18px 70px;
            font-family: ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Arial, "Apple Color Emoji", "Segoe UI Emoji";
            color: #0f172a;
          }

          /* HERO */
          .az-hero{
            position: relative;
            border-radius: 28px;
            padding: 34px 20px;
            overflow: hidden;
            background:
              radial-gradient(1200px 500px at 10% 10%, rgba(59,130,246,0.18), transparent 60%),
              radial-gradient(900px 420px at 90% 10%, rgba(168,85,247,0.16), transparent 58%),
              linear-gradient(180deg, #ffffff, #f8fafc);
            border: 1px solid rgba(15,23,42,0.08);
            box-shadow:
              0 20px 60px rgba(2, 6, 23, 0.10),
              0 2px 12px rgba(2, 6, 23, 0.06);
          }

          .az-heroInner{
            display: grid;
            grid-template-columns: 1.2fr 0.8fr;
            gap: 22px;
            align-items: center;
            position: relative;
            z-index: 2;
          }

          @media (max-width: 900px){
            .az-heroInner{ grid-template-columns: 1fr; }
          }

          .az-pill{
            display: inline-flex;
            align-items: center;
            gap: 10px;
            padding: 8px 12px;
            border-radius: 999px;
            background: rgba(2, 6, 23, 0.04);
            border: 1px solid rgba(15,23,42,0.08);
            font-weight: 700;
            font-size: 0.95rem;
            color: #0f172a;
          }

          .az-dot{
            width: 10px; height: 10px;
            border-radius: 999px;
            background: linear-gradient(135deg, #3b82f6, #a855f7);
            box-shadow: 0 0 0 3px rgba(59,130,246,0.14);
          }

          .az-title{
            margin: 14px 0 10px;
            font-size: clamp(2rem, 2.6vw, 3.1rem);
            line-height: 1.06;
            letter-spacing: -0.02em;
          }

          .az-subtitle{
            margin: 0 0 18px;
            font-size: 1.08rem;
            line-height: 1.6;
            color: rgba(15, 23, 42, 0.78);
            max-width: 62ch;
          }

          .az-ctaRow{
            display: flex;
            gap: 12px;
            flex-wrap: wrap;
            margin-bottom: 18px;
          }

          .az-btnPrimary{
            display: inline-flex;
            align-items: center;
            justify-content: center;
            gap: 10px;
            padding: 12px 16px;
            border-radius: 14px;
            text-decoration: none;
            font-weight: 800;
            color: white;
            background: linear-gradient(135deg, #2563eb, #7c3aed);
            box-shadow: 0 14px 30px rgba(37, 99, 235, 0.18);
            border: 1px solid rgba(255,255,255,0.22);
            transition: transform .15s ease, box-shadow .15s ease, filter .15s ease;
          }
          .az-btnPrimary:hover{
            transform: translateY(-1px);
            filter: brightness(1.02);
            box-shadow: 0 18px 40px rgba(37, 99, 235, 0.22);
          }

          .az-btnGhost{
            display: inline-flex;
            align-items: center;
            justify-content: center;
            padding: 12px 16px;
            border-radius: 14px;
            text-decoration: none;
            font-weight: 800;
            color: #0f172a;
            background: rgba(255,255,255,0.75);
            border: 1px solid rgba(15,23,42,0.10);
            backdrop-filter: blur(8px);
            transition: transform .15s ease, background .15s ease;
          }
          .az-btnGhost:hover{
            transform: translateY(-1px);
            background: rgba(255,255,255,0.95);
          }

          .az-trust{
            display: inline-flex;
            align-items: center;
            gap: 14px;
            padding: 12px 14px;
            border-radius: 18px;
            background: rgba(255,255,255,0.70);
            border: 1px solid rgba(15,23,42,0.08);
            backdrop-filter: blur(10px);
          }
          .az-trustItem{
            display: grid;
            line-height: 1.1;
          }
          .az-trustItem strong{ font-size: 1rem; }
          .az-trustItem span{
            font-size: 0.9rem;
            color: rgba(15,23,42,0.7);
          }
          .az-divider{
            width: 1px;
            height: 28px;
            background: rgba(15,23,42,0.12);
          }

          .az-heroCard{
            border-radius: 22px;
            background: rgba(255,255,255,0.70);
            border: 1px solid rgba(15,23,42,0.10);
            box-shadow: 0 18px 50px rgba(2, 6, 23, 0.10);
            padding: 16px;
            backdrop-filter: blur(10px);
          }
          .az-cardHead h3{
            margin: 0;
            font-size: 1.1rem;
            letter-spacing: -0.01em;
          }
          .az-cardHead p{
            margin: 6px 0 0;
            color: rgba(15,23,42,0.72);
            line-height: 1.5;
          }

          .az-miniGrid{
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 10px;
            margin-top: 12px;
          }
          .az-mini{
            padding: 12px 12px;
            border-radius: 16px;
            background: rgba(255,255,255,0.72);
            border: 1px solid rgba(15,23,42,0.08);
          }
          .az-miniLabel{
            display: block;
            font-size: 0.82rem;
            color: rgba(15,23,42,0.62);
            margin-bottom: 4px;
            font-weight: 700;
          }
          .az-miniValue{
            display: block;
            font-size: 0.98rem;
            font-weight: 900;
            letter-spacing: -0.01em;
          }

          .az-note{
            margin-top: 12px;
            padding: 12px;
            border-radius: 16px;
            background: linear-gradient(135deg, rgba(37,99,235,0.10), rgba(124,58,237,0.08));
            border: 1px solid rgba(15,23,42,0.10);
            display: flex;
            gap: 10px;
            align-items: flex-start;
            color: rgba(15,23,42,0.78);
            line-height: 1.5;
          }
          .az-noteBadge{
            display: inline-flex;
            align-items: center;
            justify-content: center;
            height: 24px;
            padding: 0 10px;
            border-radius: 999px;
            background: rgba(255,255,255,0.7);
            border: 1px solid rgba(15,23,42,0.08);
            font-weight: 900;
            font-size: 0.85rem;
            white-space: nowrap;
          }

          /* Decorative glows */
          .az-glow{
            position: absolute;
            width: 320px;
            height: 320px;
            border-radius: 999px;
            filter: blur(40px);
            opacity: 0.6;
            z-index: 1;
            pointer-events: none;
          }
          .az-glow1{
            left: -120px;
            top: -120px;
            background: rgba(59,130,246,0.35);
          }
          .az-glow2{
            right: -120px;
            top: -120px;
            background: rgba(168,85,247,0.30);
          }

          /* SECTIONS */
          .az-section{ margin-top: 34px; }

          .az-sectionHead{
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            gap: 8px;
            margin-bottom: 14px;
          }
          .az-sectionHead h2{
            margin: 0;
            font-size: 1.7rem;
            letter-spacing: -0.02em;
          }
          .az-sectionHead p{
            margin: 0;
            color: rgba(15,23,42,0.72);
            line-height: 1.55;
            /* Removed max-width for natural flow */
          }
          @media (max-width: 860px){
            .az-sectionHead{
              flex-direction: column;
              align-items: flex-start;
            }
          }

          .az-grid{
            display: grid;
            grid-template-columns: repeat(3, minmax(0, 1fr));
            gap: 14px;
          }
          @media (max-width: 980px){
            .az-grid{ grid-template-columns: repeat(2, minmax(0, 1fr)); }
          }
          @media (max-width: 620px){
            .az-grid{ grid-template-columns: 1fr; }
          }

          .az-card{
            border-radius: 20px;
            background: white;
            border: 1px solid rgba(15,23,42,0.08);
            box-shadow: 0 10px 30px rgba(2,6,23,0.06);
            padding: 16px;
            display: grid;
            grid-template-columns: 44px 1fr;
            gap: 12px;
            transition: transform .15s ease, box-shadow .15s ease;
            min-height: 124px;
          }
          .az-card:hover{
            transform: translateY(-2px);
            box-shadow: 0 18px 48px rgba(2,6,23,0.09);
          }

          .az-cardIcon{
            width: 44px;
            height: 44px;
            border-radius: 14px;
            display: grid;
            place-items: center;
            background: linear-gradient(180deg, rgba(59,130,246,0.12), rgba(168,85,247,0.10));
            border: 1px solid rgba(15,23,42,0.08);
            color: #0f172a;
          }

          .az-cardBody h3{
            margin: 0;
            font-size: 1.05rem;
            letter-spacing: -0.01em;
          }
          .az-cardBody p{
            margin: 6px 0 0;
            color: rgba(15,23,42,0.72);
            line-height: 1.55;
            font-size: 0.98rem;
          }

          .icon{ width: 22px; height: 22px; }

          /* WHY */
          .az-why{
            border-radius: 26px;
            padding: 22px;
            background:
              radial-gradient(900px 360px at 10% 20%, rgba(59,130,246,0.12), transparent 55%),
              radial-gradient(900px 360px at 90% 20%, rgba(168,85,247,0.10), transparent 55%),
              linear-gradient(180deg, #ffffff, #f8fafc);
            border: 1px solid rgba(15,23,42,0.08);
            box-shadow: 0 18px 50px rgba(2,6,23,0.07);
          }
          .az-whyInner{
            display: grid;
            grid-template-columns: 1fr;
            gap: 16px;
          }
          .az-whyText h2{
            margin: 0 0 10px;
            font-size: 1.7rem;
            letter-spacing: -0.02em;
          }
          .az-whyText p{
            margin: 0;
            color: rgba(15,23,42,0.72);
            line-height: 1.6;
            max-width: 100%; /* Allow natural wrapping, no forced line length */
            word-break: normal;
            white-space: normal;
          }
          .az-whyGrid{
            display: grid;
            grid-template-columns: repeat(2, minmax(0, 1fr));
            gap: 12px;
          }
          @media (max-width: 760px){
            .az-whyGrid{ grid-template-columns: 1fr; }
          }
          .az-whyCard{
            border-radius: 18px;
            padding: 14px;
            background: rgba(255,255,255,0.75);
            border: 1px solid rgba(15,23,42,0.08);
          }
          .az-whyCard h3{
            margin: 0 0 6px;
            font-size: 1.02rem;
            letter-spacing: -0.01em;
          }
          .az-whyCard p{
            margin: 0;
            color: rgba(15,23,42,0.72);
            line-height: 1.55;
          }

          /* ROLES */
          .az-roleGrid{
            display: grid;
            grid-template-columns: repeat(2, minmax(0, 1fr));
            gap: 14px;
          }
          @media (max-width: 820px){
            .az-roleGrid{ grid-template-columns: 1fr; }
          }

          .az-roleCard{
            border-radius: 22px;
            background: white;
            border: 1px solid rgba(15,23,42,0.08);
            box-shadow: 0 12px 36px rgba(2,6,23,0.06);
            padding: 16px;
          }
          .az-roleTitle{
            font-weight: 900;
            letter-spacing: -0.01em;
            margin-bottom: 10px;
            font-size: 1.05rem;
          }
          .az-roleList{
            margin: 0;
            padding: 0;
            list-style: none;
            display: grid;
            gap: 10px;
          }
          .az-roleList li{
            display: grid;
            grid-template-columns: 24px 1fr;
            gap: 10px;
            align-items: start;
            padding: 12px 12px;
            border-radius: 16px;
            background: rgba(255,255,255,0.75);
            border: 1px solid rgba(15,23,42,0.08);
          }
          .az-check{
            width: 24px; height: 24px;
            border-radius: 999px;
            display: grid;
            place-items: center;
            background: rgba(37,99,235,0.12);
            border: 1px solid rgba(37,99,235,0.18);
            font-weight: 900;
            line-height: 1;
          }

          .az-banner{
            margin-top: 16px;
            border-radius: 22px;
            padding: 18px;
            background: linear-gradient(135deg, rgba(37,99,235,0.12), rgba(124,58,237,0.10));
            border: 1px solid rgba(15,23,42,0.10);
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 14px;
            flex-wrap: wrap;
          }
          .az-banner h3{
            margin: 0 0 6px;
            font-size: 1.15rem;
            letter-spacing: -0.01em;
          }
          .az-banner p{
            margin: 0;
            color: rgba(15,23,42,0.72);
            line-height: 1.5;
            max-width: 70ch;
          }

          /* FINAL CTA */
          .az-finalCard{
            border-radius: 26px;
            padding: 22px;
            background:
              radial-gradient(900px 360px at 10% 20%, rgba(59,130,246,0.10), transparent 55%),
              radial-gradient(900px 360px at 90% 20%, rgba(168,85,247,0.09), transparent 55%),
              linear-gradient(180deg, #ffffff, #f8fafc);
            border: 1px solid rgba(15,23,42,0.08);
            box-shadow: 0 18px 50px rgba(2,6,23,0.07);
          }
          .az-finalCard h2{
            margin: 0 0 10px;
            font-size: 1.7rem;
            letter-spacing: -0.02em;
          }
          .az-finalCard p{
            margin: 0 0 14px;
            color: rgba(15,23,42,0.72);
            line-height: 1.6;
            max-width: 78ch;
          }
        `}</style>
      </div>
    </ServicePageShell>
  );
};

export default MicrosoftAzureServices;
